import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { Link, useLoaderData } from '@remix-run/react';
import { buildMeta } from '~/lib/seo';
import { routes } from '~/lib/routes';
import {
  getAllStatusEffects,
  getStatusEffectsByType,
  getStatusEffectsByCategory,
  getStatusEffectCategories,
} from '~/lib/game-data.server';
import type { StatusEffect } from '~/types/game-data';
import { H1, H2 } from '~/components/ui/typography';
import { Container } from '~/components/ui/container';
import { formatCategory } from '~/lib/utils';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const typeFilter = url.searchParams.get('type') as 'buff' | 'debuff' | 'state' | null;
  const categoryFilter = url.searchParams.get('category');

  let statusEffects: StatusEffect[] = getAllStatusEffects();

  if (typeFilter) {
    statusEffects = getStatusEffectsByType(typeFilter);
  } else if (categoryFilter) {
    statusEffects = getStatusEffectsByCategory(categoryFilter);
  }

  const categories = getStatusEffectCategories();

  return json({
    statusEffects,
    categories,
    currentType: typeFilter,
    currentCategory: categoryFilter,
  });
};

export const meta: MetaFunction = () =>
  buildMeta({
    title: 'Status Effects Database',
    description: 'Reference for all buffs, debuffs, and conditions in Lumora: The First Dawn.',
  });

export default function StatusEffectsPage() {
  const { statusEffects, categories, currentType, currentCategory } =
    useLoaderData<typeof loader>();

  return (
    <Container size="default" padding="large" className="space-y-8">
      <header className="space-y-3">
        <Link
          to={routes.database.index()}
          className="inline-flex items-center text-sm font-semibold text-primary hover:underline"
        >
          ← Back to Database
        </Link>
        <H1>Status Effects Reference</H1>
        <p className="text-lg text-muted-foreground">
          Browse {statusEffects.length} buffs, debuffs, and conditions.
        </p>
      </header>

      {/* Filters */}
      <div className="rounded-lg border border-border bg-card p-6 shadow">
        <H2 className="mb-4 text-lg">Filter by</H2>
        <div className="space-y-4">
          {/* Type Filter */}
          <div>
            <h3 className="mb-2 text-sm font-medium text-foreground">Type</h3>
            <div className="flex flex-wrap gap-2">
              <Link
                to={routes.database.statusEffects()}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                  !currentType && !currentCategory
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
                }`}
              >
                All
              </Link>
              <Link
                to={`${routes.database.statusEffects()}?type=buff`}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                  currentType === 'buff'
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
                }`}
              >
                Buffs
              </Link>
              <Link
                to={`${routes.database.statusEffects()}?type=debuff`}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                  currentType === 'debuff'
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
                }`}
              >
                Debuffs
              </Link>
              <Link
                to={`${routes.database.statusEffects()}?type=state`}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                  currentType === 'state'
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
                }`}
              >
                States
              </Link>
            </div>
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium text-foreground">Category</h3>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Link
                    key={category}
                    to={`${routes.database.statusEffects()}?category=${encodeURIComponent(
                      category
                    )}`}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                      currentCategory === category
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
                    }`}
                  >
                    {formatCategory(category)}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Effects List */}
      {statusEffects.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-12 text-center">
          <p className="text-lg text-muted-foreground">
            No status effects found with the selected filters.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {statusEffects.map((effect) => (
            <div
              key={effect.id}
              id={effect.id}
              className="rounded-lg border border-border bg-card p-6 shadow scroll-mt-8"
            >
              <div className="mb-3 flex items-center gap-2">
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    effect.type === 'buff'
                      ? 'bg-green-500/10 text-green-500'
                      : effect.type === 'debuff'
                      ? 'bg-red-500/10 text-red-500'
                      : 'bg-blue-500/10 text-blue-500'
                  }`}
                >
                  {effect.type === 'buff' ? 'Buff' : effect.type === 'debuff' ? 'Debuff' : 'State'}
                </span>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                  {formatCategory(effect.category)}
                </span>
                {effect.can_stack && (
                  <span className="rounded-full bg-yellow-500/10 px-3 py-1 text-xs font-semibold text-yellow-500">
                    Stackable
                  </span>
                )}
              </div>

              <h3 className="text-2xl font-semibold text-foreground">{effect.name}</h3>
              <p className="mt-2 text-base text-muted-foreground">{effect.description}</p>

              {effect.special_notes && (
                <div className="mt-4 rounded-lg border border-border bg-background p-4">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Note:</span> {effect.special_notes}
                  </p>
                </div>
              )}

              {/* Max Degree/Level */}
              {(effect.degrees || effect.levels) && (() => {
                const degreesOrLevels = effect.degrees || effect.levels || {};
                const numericKeys = Object.keys(degreesOrLevels).filter(key => !isNaN(Number(key)));
                const maxDegree = numericKeys.length > 0
                  ? Math.max(...numericKeys.map(Number))
                  : null;

                return maxDegree !== null ? (
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">
                      Max {effect.degrees ? 'Degree' : 'Level'}:
                    </span>
                    <span className="rounded-full border border-border bg-background px-3 py-1 text-sm font-medium text-foreground">
                      {maxDegree}
                    </span>
                  </div>
                ) : null;
              })()}

              {/* Contexts */}
              {effect.contexts && effect.contexts.length > 0 && (
                <div className="mt-4">
                  <h4 className="mb-2 text-sm font-semibold text-foreground">Contexts</h4>
                  <div className="flex flex-wrap gap-2">
                    {effect.contexts.map((context) => (
                      <span
                        key={context}
                        className="rounded-full border border-border bg-background px-2 py-1 text-xs text-muted-foreground"
                      >
                        {context}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Range Types */}
              {effect.range_types && effect.range_types.length > 0 && (
                <div className="mt-4">
                  <h4 className="mb-2 text-sm font-semibold text-foreground">Range Types</h4>
                  <div className="flex flex-wrap gap-2">
                    {effect.range_types.map((rangeType) => (
                      <span
                        key={rangeType}
                        className="rounded-full border border-border bg-background px-2 py-1 text-xs text-muted-foreground"
                      >
                        {rangeType}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Container>
  );
}
